import { useCallback } from 'react'
import ApiClient from 'api-client/ApiClient'

export const ENABLE_SIMPLE_NETWORK_BATCH_CREATION = false

/** Map of networkId → portId */
export type PortsMap = Record<string, string>
export type SimpleNetworkPort = PortsMap

export const useSimpleNetworkPorts = () => {
  const getNeutron = () => ApiClient.getInstance().neutron

  const deletePort = async (portId: string): Promise<void> => {
    try {
      await getNeutron().deletePort(portId)
      console.log(`[Port] Deleted: ${portId}`)
    } catch (error) {
      console.error(`[Port] Delete failed: ${portId}`, error)
    }
  }

  const deletePorts = useCallback(async (ports: PortsMap): Promise<void> => {
    for (const portId of Object.values(ports)) {
      await deletePort(portId)
    }
  }, [])

  const createPortsForVM = async (
    simpleNetworks: any[],
    tenantId: string,
    vmIndex?: number,
    vmName?: string,
  ): Promise<PortsMap> => {
    const ports: PortsMap = {}

    for (const network of simpleNetworks) {
      const portName =
        vmIndex !== undefined
          ? `${vmName}-${vmIndex + 1}-${network?.name}-port`
          : `${vmName}-${network?.name}-port`
      const response = await getNeutron().createPort({
        port: {
          network_id: network.id,
          tenant_id: tenantId,
          admin_state_up: true,
          name: portName,
          port_security_enabled: false,
          'binding:profile': {
            'l2-true': true,
          },
        },
      })

      const portId = response?.port?.id || response?.id
      if (!portId) throw new Error(`Port creation failed for ${network.name}`)

      ports[network.id] = portId
      console.log(`[Port] Created: ${network.name} → ${portId}`)
    }

    return ports
  }

  const preCreatePorts = useCallback(
    async (
      networks: any[],
      vmCount: number,
      tenantId: any,
      vmName: string,
    ): Promise<PortsMap | PortsMap[]> => {
      const simpleNetworks = networks.filter((n) => n.isSimpleNetwork)
      if (simpleNetworks.length === 0) {
        return {}
      }

      const allCreatedPorts: PortsMap[] = []

      try {
        for (let i = 0; i < vmCount; i++) {
          const vmPorts = await createPortsForVM(
            simpleNetworks,
            tenantId,
            vmCount > 1 ? i : undefined,
            vmName,
          )
          allCreatedPorts.push(vmPorts)
        }
        return vmCount === 1 ? allCreatedPorts[0] : allCreatedPorts
      } catch (error) {
        console.error('[Port] Creation failed, rolling back...')
        for (const ports of allCreatedPorts) {
          await deletePorts(ports)
        }
        throw error
      }
    },
    [],
  )

  const hasSimpleNetworks = useCallback(
    (networks: any[]) => networks.some((n) => n.isSimpleNetwork),
    [],
  )

  const shouldEnableConfigDrive = useCallback(
    (networks: any[], cloudInitEnabled: boolean) =>
      cloudInitEnabled &&
      networks.some(
        (n) => n.isSimpleNetwork || n.subnetDetails?.some((s: any) => s.enable_dhcp === false),
      ),
    [],
  )

  return {
    preCreateSimpleNetworkPorts: preCreatePorts,
    cleanupPreCreatedPorts: deletePorts,
    hasSimpleNetworks,
    shouldEnableConfigDrive,
    ENABLE_SIMPLE_NETWORK_BATCH_CREATION,
  }
}

export default useSimpleNetworkPorts
